import * as Sentry from '@sentry/nextjs';
import {
  BridgeTokenRequestError,
  exchangeBridgeToken,
  getBridgeToken,
  refreshAnonymousBridgeToken,
  refreshUserBridgeToken,
} from './bridgeToken';
import {
  BridgeTokenKind,
  bumpBridgeLogoutEpoch,
  deleteAnonymousBridgeTokens,
  deleteUserBridgeTokens,
  isValidToken,
  readBridgeAccessToken,
  readBridgeLogoutEpoch,
  readBridgeRefreshToken,
  readBridgeTokenSubject,
  storeBridgeTokenSubject,
} from './bridgeTokenCookies';

export type EnsureBridgeTokenParams = {
  /** USG origin without `/api`, e.g. `https://staging.services.unity.com`. */
  usgBaseUrl: string;
  /** Unity ID JWT (`unity_token`) from the session; omit for a guest token. */
  unityToken?: string;
  /** Genesis user the token is for; a stored token bound to anyone else is dropped. */
  userId?: string;
  /** Guest cart to carry into the signed-in cart via token exchange. */
  cartId?: string;
};

// Heterogeneous by design: a provision resolves a token, a renewal resolves
// the token plus the kind it came from.
const inFlight = new Map<string, Promise<unknown>>();

/**
 * Bumped whenever the session ends. A mint that was already in the air when
 * that happened must not write the old session's token back into the cookies.
 * The cookies are shared by every tab, so the marker has to be too: a counter
 * in this module would only invalidate work started in the tab that signed out.
 */
let generation = 0;

/**
 * Provisioning writes the shared cookie jar, and bridgeToken.ts commits from
 * inside the request, so two overlapping attempts cannot be untangled after
 * the fact: a stale one would overwrite the live token and then delete it on
 * its way out. Attempts therefore queue, and each reads the sign-out marker
 * only once the previous one has finished cleaning up after itself.
 */
let commitQueue: Promise<unknown> = Promise.resolve();

const COMMIT_LOCK = 'compass-bridge-token-commit';

/**
 * `commitQueue` orders this tab, but the cookies and the logout epoch are
 * shared browser-wide, so ordering one tab is not enough: a mint still in the
 * air in a backgrounded tab could land on top of a newer sign-in and then
 * delete it on its epoch check. Web Locks is the one same-origin primitive
 * that spans tabs; where it is missing the in-tab queue is all there is.
 */
const withCommitLock = <T>(run: () => Promise<T>): Promise<T> =>
  typeof navigator !== 'undefined' && navigator.locks?.request
    ? navigator.locks.request(COMMIT_LOCK, run)
    : run();

const tokenKind = (unityToken: string | undefined): BridgeTokenKind =>
  isValidToken(unityToken) ? 'user' : 'anonymous';

/**
 * The subject the request path may use a stored user token for. A module-level
 * interceptor cannot call `useSession`, and the live Genesis user sits in the
 * httpOnly NextAuth JWT, so the session hook has to publish it here.
 *
 * `known` is separate from the value on purpose: before the session resolves
 * we cannot tell a token bound to someone else from a perfectly good one, and
 * deleting on that guess would wipe a valid credential on every page load.
 */
let activeSubject: {
  known: boolean;
  userId: string | undefined;
  unityToken: string | undefined;
} = {
  known: false,
  userId: undefined,
  unityToken: undefined,
};

export const publishActiveBridgeTokenSubject = ({
  userId,
  unityToken,
}: {
  userId: string | undefined;
  /** The gateway wants this on bridge routes too, not just on the mint. */
  unityToken: string | undefined;
}) => {
  // Module state on the server is shared by every concurrent request, so an
  // SSR render would leak one visitor's bearer into another's. This only ever
  // serves the browser interceptor; guarded here rather than at the call site
  // so no future caller can reintroduce the leak.
  if (typeof window === 'undefined') {
    return;
  }
  activeSubject = {
    known: true,
    userId: isValidToken(userId) ? userId : undefined,
    unityToken: isValidToken(unityToken) ? unityToken : undefined,
  };
};

/** Test seam: back to "session not resolved yet". */
export const resetActiveBridgeTokenSubject = () => {
  activeSubject = { known: false, userId: undefined, unityToken: undefined };
};

/**
 * Identity gate for request paths that no mount gate ran for — a React Query
 * refetch on window focus, say. `ensureBridgeToken` drops another session's
 * token before it provisions, but the interceptor reads the cookies directly,
 * so without this a page whose session has already flipped to another user
 * would send the previous user's bearer and read their cart.
 *
 * Deletes only on a proven mismatch. An unresolved session is not evidence of
 * one, and deleting on that guess would wipe a valid credential on every load.
 */
export const discardUserBridgeTokensFromAnotherSession = () => {
  if (!activeSubject.known) {
    return;
  }
  const hasUserToken =
    isValidToken(readBridgeAccessToken('user')) ||
    isValidToken(readBridgeRefreshToken('user'));
  if (!hasUserToken) {
    return;
  }
  const subject = readBridgeTokenSubject();
  if (
    activeSubject.userId === undefined ||
    !isValidToken(subject) ||
    subject !== activeSubject.userId
  ) {
    deleteUserBridgeTokens();
  }
};

/**
 * Whether a stored user token is proven to belong to the live session. Unknown
 * fails closed: not deleting an unverified token is right, but sending one is
 * not, so the request path withholds it until the session says whose it is.
 */
const canUseUserToken = (): boolean =>
  activeSubject.known &&
  activeSubject.userId !== undefined &&
  readBridgeTokenSubject() === activeSubject.userId;

/** Which credential a request ended up with, so callers pair headers to it. */
type SelectedBridgeToken = { kind: BridgeTokenKind; token: string };

/** The stored token this session is allowed to send, user first, else guest. */
const usableStoredBridgeToken = (): SelectedBridgeToken | undefined => {
  if (canUseUserToken()) {
    const userToken = readBridgeAccessToken('user');
    if (isValidToken(userToken)) {
      return { kind: 'user', token: userToken };
    }
  }
  const anonymousToken = readBridgeAccessToken('anonymous');
  return isValidToken(anonymousToken)
    ? { kind: 'anonymous', token: anonymousToken }
    : undefined;
};

export type BridgeRequestAuthorization = {
  /** `X-Authorization`: the bridge token the commerce endpoints read. */
  xAuthorization: string;
  /** `Authorization`: the gateway rejects bridge routes without it. */
  authorization?: string;
};

/**
 * Header values for a bridge request, for the interceptor. Withholds a user
 * token this session cannot prove is its own and falls back to the guest
 * token, so an unverified bearer never leaves the browser.
 *
 * The two travel together: the gateway authenticates the caller from
 * `Authorization` — the same `unity_token` the proxy this replaced sent — and
 * only then reads the bridge token, so a user bridge token paired with no
 * `Authorization` is the 401 this exists to avoid.
 */
export const bridgeAuthorizationForRequest = async (
  usgBaseUrl: string
): Promise<BridgeRequestAuthorization | undefined> => {
  discardUserBridgeTokensFromAnotherSession();
  const selected =
    usableStoredBridgeToken() ?? (await renewBridgeTokenForRequest(usgBaseUrl));
  if (!selected) {
    return undefined;
  }
  // Keyed off the credential actually selected, not off the subject cookie:
  // an expired user access cookie falls back to the guest token, and pairing
  // the user's `Authorization` with that would be a mismatched request.
  const pairsWithUser =
    selected.kind === 'user' && isValidToken(activeSubject.unityToken);
  return {
    xAuthorization: `Bearer ${selected.token}`,
    ...(pairsWithUser
      ? { authorization: `Bearer ${activeSubject.unityToken}` }
      : {}),
  };
};

/**
 * Bridge cookies outlive the session that minted them: the auth refresh
 * middleware drops the NextAuth cookie on a failed Genesis refresh without
 * going through our signOut teardown. Reusing what is left would hand the next
 * sign-in the previous user's credential, so anything not bound to the current
 * Genesis user is discarded before it can be read.
 */
const dropTokensFromAnotherSession = (
  kind: BridgeTokenKind,
  userId: string | undefined
) => {
  const subject = readBridgeTokenSubject();
  if (kind === 'anonymous') {
    if (subject !== undefined) {
      deleteUserBridgeTokens();
    }
    return;
  }
  if (!isValidToken(userId) || subject !== userId) {
    deleteUserBridgeTokens();
  }
};

/**
 * A 4xx that is the service rejecting the credential itself. 408 and 429 are
 * about this moment, not the credential: treating them as spent throws away a
 * token that would work on the next try, and on the guest path that means a
 * new identity and a stranded cart.
 */
const RETRYABLE_STATUSES = new Set([408, 429]);

/** A refresh the service actively rejected; anything else may still succeed. */
const isSpentCredential = (error: unknown) =>
  error instanceof BridgeTokenRequestError &&
  error.status !== undefined &&
  error.status >= 400 &&
  error.status < 500 &&
  !RETRYABLE_STATUSES.has(error.status);

/** Exchange needs a live guest token; a refresh saves the cart from a mint. */
const anonymousTokenForExchange = async (
  usgBaseUrl: string
): Promise<string | undefined> => {
  const stored = readBridgeAccessToken('anonymous');
  if (isValidToken(stored)) {
    return stored;
  }
  if (!isValidToken(readBridgeRefreshToken('anonymous'))) {
    return undefined;
  }
  try {
    const { access_token } = await refreshAnonymousBridgeToken({ usgBaseUrl });
    return access_token;
  } catch (error) {
    // Only a rejected credential means the guest token is unrecoverable.
    // Swallowing a 5xx here would mint instead and silently strand the cart.
    if (!isSpentCredential(error)) {
      throw error;
    }
    return undefined;
  }
};

const provision = async ({
  usgBaseUrl,
  unityToken,
  cartId,
}: EnsureBridgeTokenParams): Promise<string> => {
  const kind = tokenKind(unityToken);

  // Login with a guest cart in hand: exchange carries the cart over, where a
  // plain mint would strand it.
  if (
    kind === 'user' &&
    isValidToken(cartId) &&
    (await anonymousTokenForExchange(usgBaseUrl))
  ) {
    const { access_token } = await exchangeBridgeToken({
      usgBaseUrl,
      unityToken,
      cartId,
    });
    return access_token;
  }

  if (isValidToken(readBridgeRefreshToken(kind))) {
    try {
      const { access_token } =
        kind === 'user'
          ? await refreshUserBridgeToken({ usgBaseUrl })
          : await refreshAnonymousBridgeToken({ usgBaseUrl });
      return access_token;
    } catch (error) {
      // Only a rejected credential means this one is unrecoverable. Minting
      // past a 5xx would hand a guest a brand-new identity and strand the
      // cart their old token still points at; a user mint keys to the same
      // identity, so falling through is harmless there.
      if (kind === 'anonymous' && !isSpentCredential(error)) {
        throw error;
      }
    }
  }

  const { access_token } = await getBridgeToken({
    usgBaseUrl,
    unityToken: kind === 'user' ? unityToken : undefined,
  });
  return access_token;
};

/**
 * Whether a bridge token that this session may actually use is in the cookies
 * right now. Gates must read this rather than remember an earlier outcome: the
 * access-token cookie expires on its own, so a remembered "ready" outlives it.
 */
export const hasUsableBridgeToken = (userId: string | undefined): boolean =>
  isValidToken(readBridgeAccessToken('user')) &&
  isValidToken(userId) &&
  readBridgeTokenSubject() === userId;

/**
 * Resolves the bridge token the `X-Authorization` interceptor reads, reusing a
 * live one bound to this session and otherwise refreshing, exchanging or
 * minting.
 *
 * Resolves to `undefined` instead of rejecting: callers must be able to hold
 * the request back rather than fire an unauthenticated bridge call.
 */
const runFenced = <T>(
  key: string,
  work: () => Promise<T>,
  onCommitted?: () => void
): Promise<T | undefined> => {
  const pending = inFlight.get(key);
  if (pending) {
    return pending as Promise<T | undefined>;
  }

  // Captured now, not after the queue: a sign-out between this call and this
  // attempt's turn is exactly what the fence has to notice.
  const startedAt = generation;
  const startedAtEpoch = readBridgeLogoutEpoch();

  const isStale = () =>
    startedAt !== generation || startedAtEpoch !== readBridgeLogoutEpoch();

  const attempt: Promise<T | undefined> = (async () => {
    await commitQueue;
    return withCommitLock(async () => {
      // Already overtaken while queued or waiting for the lock: return without
      // running the work at all, because `work()` writes the shared cookies as
      // a side effect and would clobber whatever holds them now.
      if (isStale()) {
        return undefined;
      }
      const token = await work();
      // bridgeToken.ts writes the cookies itself, so a session that ended
      // mid-flight — here or in another tab — is cleaned up after the fact.
      // The lock is what makes that safe: no other tab can have committed
      // since the check above, so this deletes only what this attempt wrote.
      if (isStale()) {
        deleteUserBridgeTokens();
        return undefined;
      }
      onCommitted?.();
      return token;
    });
  })()
    .catch((error) => {
      // BridgeTokenRequestError already drops the bearer headers axios would
      // otherwise carry into this payload.
      Sentry.captureException(error);
      return undefined;
    })
    .finally(() => {
      // A reset can have replaced this entry while it was settling; removing
      // the newer one would send the next caller off to provision again.
      if (inFlight.get(key) === attempt) {
        inFlight.delete(key);
      }
    });

  inFlight.set(key, attempt);
  commitQueue = attempt.catch(() => undefined);
  return attempt;
};

export const ensureBridgeToken = (
  params: EnsureBridgeTokenParams
): Promise<string | undefined> => {
  const { usgBaseUrl, unityToken, userId, cartId } = params;
  const kind = tokenKind(unityToken);

  dropTokensFromAnotherSession(kind, userId);

  const existing = readBridgeAccessToken(kind);
  if (isValidToken(existing)) {
    return Promise.resolve(existing);
  }

  // Callers that would provision different tokens must not share a result, so
  // only genuinely equivalent work is deduplicated.
  return runFenced(
    JSON.stringify([usgBaseUrl, kind, userId ?? '', cartId ?? '']),
    () => provision(params),
    () => {
      if (kind === 'user' && isValidToken(userId)) {
        storeBridgeTokenSubject(userId);
      }
    }
  );
};

/**
 * Last line for bridge requests no gate triggered — a React Query refetch on
 * window focus, say, under a page left mounted past the access token's
 * lifetime. Only the refresh cookie is involved, which outlives the access
 * token by days and needs no session context, so this is safe to call from
 * the request interceptor. Returns undefined when there is nothing to renew,
 * leaving the caller to degrade exactly as before.
 */
export const renewBridgeTokenForRequest = (
  usgBaseUrl: string
): Promise<SelectedBridgeToken | undefined> => {
  discardUserBridgeTokensFromAnotherSession();
  const current = usableStoredBridgeToken();
  if (current) {
    return Promise.resolve(current);
  }

  // Renewing the user credential is itself a use of it, so it waits on the
  // same proof; the guest token carries no identity and never does.
  const kind: BridgeTokenKind | undefined =
    canUseUserToken() && isValidToken(readBridgeRefreshToken('user'))
      ? 'user'
      : isValidToken(readBridgeRefreshToken('anonymous'))
        ? 'anonymous'
        : undefined;
  if (!kind) {
    return Promise.resolve(undefined);
  }

  return runFenced(JSON.stringify(['renew', usgBaseUrl, kind]), async () => {
    try {
      const { access_token } =
        kind === 'user'
          ? await refreshUserBridgeToken({ usgBaseUrl })
          : await refreshAnonymousBridgeToken({ usgBaseUrl });
      return { kind, token: access_token };
    } catch (error) {
      // A refresh token the service rejected stays rejected, and the cookie
      // outlives the access token by days: leaving it would re-run this
      // doomed request, and report it, on every later bridge request.
      if (isSpentCredential(error)) {
        if (kind === 'user') {
          deleteUserBridgeTokens();
        } else {
          deleteAnonymousBridgeTokens();
        }
      }
      throw error;
    }
  });
};

/**
 * Sign-out teardown: discards in-flight provisioning, in this tab and in any
 * other, so a mint already in the air cannot restore the token after the
 * session has gone.
 */
export const resetBridgeTokenProvisioning = () => {
  generation += 1;
  bumpBridgeLogoutEpoch();
  inFlight.clear();
};
